import {
  UseQueryOptions,
  useMutation,
  useQueries,
  useQuery
} from '@tanstack/react-query';
import { HTTPError } from 'ky';

import { queryClient } from '@/adapters/query';
import { crmScreenerApiClient } from '@/adapters/schemaClients';
import { GetFacilitySettings200Response } from '@/types/crm-screener-api';
import { UpdateFacilitySettingRequest } from '@/types/crm-screener-api/apis/DefaultApi';

const FACILITY_SETTINGS_QUERY_KEY = 'screenerFacilitySettings';

export const useFacilitySettingsQuery = () => {
  const invalidate = (facilityId?: string) => {
    if (facilityId) {
      queryClient.invalidateQueries([FACILITY_SETTINGS_QUERY_KEY, facilityId]);
    } else {
      queryClient.invalidateQueries([FACILITY_SETTINGS_QUERY_KEY]);
    }
  };

  return {
    useGetFacilitySettings: (
      facilityId: string,
      options: UseQueryOptions<
        GetFacilitySettings200Response['data'],
        HTTPError
      > = {}
    ) =>
      useQuery<GetFacilitySettings200Response['data'], HTTPError>(
        [FACILITY_SETTINGS_QUERY_KEY, facilityId],
        async () => {
          const response = await crmScreenerApiClient.getFacilitySettings({
            facilityId: facilityId
          });
          return response.data;
        },
        {
          enabled: !!facilityId,
          ...options
        }
      ),

    mutations: {
      updateFacilitySetting: useMutation(
        (params: UpdateFacilitySettingRequest) =>
          crmScreenerApiClient.updateFacilitySetting(params),
        {
          onSuccess: (_data, variables) => {
            invalidate(variables.facilityId);
          }
        }
      )
    }
  };
};

export const useFacilitySettingsQueries = (facilityIds: string[]) => {
  return useQueries({
    queries: facilityIds.map((facilityId) => ({
      queryKey: [FACILITY_SETTINGS_QUERY_KEY, facilityId],
      queryFn: async () => {
        const response = await crmScreenerApiClient.getFacilitySettings({
          facilityId
        });
        return response.data;
      },
      enabled: !!facilityId
    }))
  });
};
